import React, { useState } from "react";
import { api, LOGO_URL } from "../lib/api";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackNewsletterSignup } from "../lib/analytics";

const EmailCapture = ({ source = "homepage", variant = "light" }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    try {
      await api.post("/subscribe", { email, source });
      setStatus("success");
      trackNewsletterSignup(source);
      toast.success("You're in! We'll send the best Bedford deals to your inbox.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  const isDark = variant === "dark";
  return (
    <section
      data-testid={`email-capture-${source}`}
      className={`relative overflow-hidden ${
        isDark ? "bg-[#0B2C5B] text-white" : "bg-gradient-to-br from-[#FFF7E8] via-white to-[#E6F6FD]"
      }`}
    >
      {/* subtle wave background */}
      <svg
        className="absolute inset-x-0 bottom-0 w-full opacity-30"
        viewBox="0 0 1440 220"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,120 C320,200 640,40 960,100 C1200,140 1320,180 1440,140 L1440,220 L0,220 Z"
          fill={isDark ? "#2AB7E8" : "#9FD8F0"}
          opacity="0.4"
        />
      </svg>

      <div className="container-site relative z-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 flex flex-col items-start">
          {isDark ? (
            <div className="inline-block bg-white rounded-2xl p-3 mb-6 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
              <img src={LOGO_URL} alt="Visit Bedford" className="h-20 md:h-24 w-auto block" />
            </div>
          ) : (
            <img src={LOGO_URL} alt="Visit Bedford" className="h-20 md:h-24 w-auto mb-6" />
          )}
          <div className={`text-xs font-semibold tracking-[0.25em] uppercase mb-4 ${isDark ? "text-[#9FD8F0]" : "text-[#2AB7E8]"}`}>
            Insider Guide — Free
          </div>
          <h2 className={`font-serif-display text-4xl md:text-5xl font-bold leading-[1.05] text-balance ${isDark ? "text-white" : "text-[#0B2C5B]"}`}>
            Get the Best Deals for Universal Studios Bedford.
          </h2>
        </div>

        <div className="md:col-span-7">
          <div className={`rounded-3xl p-8 md:p-10 border ${
            isDark ? "bg-white/5 backdrop-blur-xl border-white/10" : "bg-white shadow-[0_20px_60px_rgba(11,44,91,0.08)] border-slate-100"
          }`}>
            <p className={`text-lg leading-relaxed mb-6 ${isDark ? "text-white/80" : "text-slate-700"}`}>
              Be the first to hear about:
            </p>
            <ul className={`space-y-2.5 mb-8 text-base ${isDark ? "text-white/80" : "text-slate-700"}`}>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#FF8C00]" /> Exclusive hotel deals near the park</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#FF8C00]" /> Opening date &amp; ticket updates</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#FF8C00]" /> Local travel tips from Bedford insiders</li>
            </ul>

            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="subscribe-form">
              <div className={`flex-1 flex items-center gap-3 rounded-full px-5 py-4 border ${
                isDark ? "bg-white/10 border-white/20" : "bg-[#FAFAFA] border-slate-200"
              }`}>
                <Mail className={`w-5 h-5 ${isDark ? "text-[#9FD8F0]" : "text-[#2AB7E8]"}`} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="subscribe-email-input"
                  className={`flex-1 bg-transparent outline-none placeholder:opacity-60 ${
                    isDark ? "text-white placeholder:text-white/50" : "text-[#0B2C5B]"
                  }`}
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="subscribe-submit-btn"
                className="btn-primary-gradient text-white rounded-full px-7 py-4 font-semibold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
                Get Updates &amp; Deals
              </button>
            </form>
            <p className={`text-xs mt-4 ${isDark ? "text-white/50" : "text-slate-500"}`}>
              No spam. Unsubscribe anytime. We respect your inbox.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmailCapture;
